import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";
import { CloseRoundedIcon } from "../../utilities/icons";
import {
  formatActivityLabel,
  formatActivityTimestamp,
  getActivityColor,
  getActivityTargetName,
} from "../../utilities/activityHelpers";

function formatKey(label) {
  return label
    .replaceAll("_", " ")
    .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

function renderValue(value) {
  if (value === null || value === undefined || value === "") {
    return "NA";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "[]";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

function DetailGrid({ title, data, preserveWhitespace = false }) {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value !== undefined,
  );

  if (!entries.length) {
    return null;
  }

  return (
    <div className="ax-activity-section">
      <Typography className="ax-activity-section-title">{title}</Typography>
      <div className="ax-activity-detail-grid">
        {entries.map(([key, value]) => (
          <div className="ax-activity-detail-card" key={`${title}-${key}`}>
            <div className="ax-activity-detail-label">{formatKey(key)}</div>
            <pre
              className={`ax-activity-detail-value${
                preserveWhitespace ? " is-pre" : ""
              }`}
            >
              {renderValue(value)}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityDetailsDrawer({ open, onClose, activity }) {
  const colorClass = getActivityColor(activity?.action);
  const timestamp = formatActivityTimestamp(activity?.timestamp);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="ax-drawer"
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 720 },
          background: "var(--ax-surface)",
        },
      }}
    >
      <div className="ax-activity-drawer">
        <div className="ax-activity-drawer-head">
          <div>
            <Typography
              className="ax-card-title"
              sx={{
                fontFamily: "var(--ax-font-heading)",
                fontWeight: 500,
                fontSize: "1.05rem",
                letterSpacing: "-0.015em",
              }}
            >
              Activity Details
            </Typography>
            <Typography
              className="ax-muted"
              variant="body2"
              sx={{ mt: 0.5 }}
            >
              Full read-only audit record from database
            </Typography>
          </div>
          <Button
            onClick={onClose}
            startIcon={<CloseRoundedIcon />}
            className="ax-activity-close"
            variant="outlined"
            size="small"
          >
            Close
          </Button>
        </div>

        {activity ? (
          <Box className="ax-activity-drawer-body">
            <div className="ax-activity-summary-card">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={2}
              >
                <div>
                  <Typography
                    variant="h6"
                    sx={{
                      textTransform: "capitalize",
                      fontFamily: "var(--ax-font-heading)",
                      fontWeight: 500,
                      fontSize: "1.05rem",
                      letterSpacing: "-0.015em",
                      color: "var(--ax-ink)",
                    }}
                  >
                    {formatActivityLabel(activity.action)}
                  </Typography>
                  <Typography
                    className="ax-muted"
                    variant="body2"
                    sx={{ mt: 0.5 }}
                  >
                    {activity.entityType || "entity"} updated on {timestamp}
                  </Typography>
                </div>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={activity.status || "success"}
                    className={`ax-activity-chip ${colorClass}`}
                    size="small"
                  />
                  <Chip
                    label={getActivityTargetName(activity)}
                    className="ax-activity-chip neutral"
                    size="small"
                  />
                </Stack>
              </Stack>
            </div>

            <DetailGrid title="Actor" data={activity.actor} />
            <DetailGrid title="Target" data={activity.target} />
            <DetailGrid title="Metadata" data={activity.metadata} preserveWhitespace />
            <DetailGrid title="Request" data={activity.request} />

            <div className="ax-activity-before-after">
              <div className="ax-activity-state-panel">
                <Typography className="ax-activity-section-title">
                  Before
                </Typography>
                <pre className="ax-activity-json">
                  {JSON.stringify(activity.before || {}, null, 2)}
                </pre>
              </div>
              <div className="ax-activity-state-panel">
                <Typography className="ax-activity-section-title">
                  After
                </Typography>
                <pre className="ax-activity-json">
                  {JSON.stringify(activity.after || {}, null, 2)}
                </pre>
              </div>
            </div>

            <Divider sx={{ my: 1 }} />

            <div className="ax-activity-section">
              <Typography className="ax-activity-section-title">
                Raw Database Record
              </Typography>
              <pre className="ax-activity-json">
                {JSON.stringify(activity, null, 2)}
              </pre>
            </div>
          </Box>
        ) : null}
      </div>
    </Drawer>
  );
}

export default ActivityDetailsDrawer;
